import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { decisionErrorMessage } from '../lib/docReviewLogic.mjs';
import {
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  FileCheck2,
  Gauge,
  LibraryBig,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import { Empty, SkeletonTableRows } from '../ui/kit';
import { nurDatum } from '../lib/zeit';
import './Vehicles.css';
import PageHeader from '../components/PageHeader';
import { ReviewDrawerById } from '../components/ReviewDrawer';   // FAHRER-KI-ABLAUFDATEN-BEDIENUNG-001: Prüfbereich bleibt im Reiter

const CLASS_LABELS = {
  standard: 'VOOGO Basis',
  comfort: 'VOOGO Komfort',
  premium: 'VOOGO Business',
  xl: 'VOOGO Family',
  max: 'VOOGO Van',
  taxi: 'VOOGO Taxi',
};

const CLASS_OPTS = ['standard', 'comfort', 'premium', 'xl', 'max'];

const STATUS = {
  approved: { label: 'Genehmigt', color: '#547860' },
  pending: { label: 'In Prüfung', color: '#7a6680' },
  rejected: { label: 'Abgelehnt', color: '#a15a5a' },
  suspended: { label: 'Gesperrt', color: '#b45309' },   // ADMIN-FAHRZEUG-PARTNER-SPERRE-001: vom Admin gesperrt (approved ⇄ suspended)
  retired: { label: 'Stillgelegt', color: '#6b7280' },   // KENNZEICHEN-EINDEUTIG-001: vom Partner/Admin entfernt, Zeile als Beleg erhalten
};

const VEHICLE_DOCUMENTS = [
  { type: 'konzession', label: 'Konzession' },
  { type: 'zulassung', label: 'Zulassungsbescheinigung Teil I (Fahrzeugschein)' },
];

const FILTERS = [
  { key: 'pending', label: 'In Prüfung', hint: 'Offene Fahrzeugfreigaben' },
  { key: 'approved', label: 'Genehmigt', hint: 'Aktiver Fahrzeugbestand' },
  { key: 'rejected', label: 'Abgelehnt', hint: 'Fahrzeuge mit Nachbesserung' },
  { key: 'suspended', label: 'Gesperrt', hint: 'Vom Admin gesperrt' },   // ADMIN-FAHRZEUG-PARTNER-SPERRE-001
  // KENNZEICHEN-EINDEUTIG-001: entfernte Fahrzeuge (Kennzeichen wieder frei) nur auf Wunsch.
  { key: 'retired', label: 'Stillgelegt', hint: 'Entfernt (Partner oder Admin)' },
  // Owner-Entscheid 26.08.: der Arbeitsbereich ohne Status ist jetzt die Browse-Gesamtliste
  // (blättern + optional suchen) statt reiner Pflichtsuche.
  { key: '', label: 'Alle Fahrzeuge', hint: 'Gesamtbestand blättern oder durchsuchen' },
];

const initials = value => String(value || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0))
  .join('')
  .toUpperCase();

const latestVehicleDocument = (documents, documentType) => [...(documents || [])]
  .filter(document => document.document_type === documentType)
  .sort((left, right) => {
    const revision = Number(right.revision || 1) - Number(left.revision || 1);
    if (revision !== 0) return revision;

    const createdAt = String(right.created_at || '').localeCompare(String(left.created_at || ''));
    if (createdAt !== 0) return createdAt;

    return String(right.id || '').localeCompare(String(left.id || ''));
  })[0] || null;

const VehicleDocumentEntry = ({ document, label, onOpen }) => {
  const status = STATUS[document?.status];

  return (
    <div className="vehicles-document-entry">
      <strong>{label}</strong>
      {document ? (
        <div className={`vehicles-document-cell is-${document.status}`}>
          {document.status === 'approved' && <CheckCircle2 size={13} />}
          {document.status === 'pending' && <Clock3 size={13} />}
          {document.status === 'rejected' && <XCircle size={13} />}
          <div>
            {document.has_private_asset ? (
              <button onClick={onOpen}>{document.display_name || label}</button>
            ) : (
              <span>Kein Inhalt</span>
            )}
            <small>{status?.label || document.status || 'Status unbekannt'}</small>
          </div>
        </div>
      ) : (
        <span className="vehicles-no-document">Nicht hochgeladen</span>
      )}
    </div>
  );
};

const CatalogInput = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <label className="vehicles-catalog-field">
    <span>{label}</span>
    <input
      type={type}
      aria-label={label}
      value={value}
      onChange={event => onChange(event.target.value)}
      placeholder={placeholder}
    />
  </label>
);

// ⌘K-Palette: /vehicles-admin?search=<begriff> startet direkt die Bestandssuche und
// /vehicles-admin?browse=1 die Browse-Gesamtliste (beides Arbeitsbereich „Alle Fahrzeuge").
// Ohne Parameter bleibt der Startzustand exakt wie bisher (Prüfqueue).
const urlVehicleSearch = () => {
  try { return (new URLSearchParams(window.location.search).get('search') || '').trim(); } catch { return ''; }
};
const urlVehicleBrowse = () => {
  try { return Boolean(new URLSearchParams(window.location.search).get('browse')); } catch { return false; }
};

export default function Vehicles() {
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState(() => ((urlVehicleSearch() || urlVehicleBrowse()) ? '' : 'pending'));
  const [searchDraft, setSearchDraft] = useState(urlVehicleSearch);
  const [appliedSearch, setAppliedSearch] = useState(urlVehicleSearch);
  // Echte Paginierung (Owner-Entscheid 26.08.): Server liefert { vehicles, total, limit, offset }.
  // total === null heißt „altes Backend ohne Paginierungsfelder" → tolerant im Alt-Verhalten.
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(null);

  // Spätere ⌘K-Navigationen mit ?search= bzw. ?browse=1 (auch von dieser Seite aus) übernehmen
  // Begriff/Modus; der Mount ist über die Startwerte oben abgedeckt.
  const skipFirstLocationRef = useRef(true);
  useEffect(() => {
    if (skipFirstLocationRef.current) { skipFirstLocationRef.current = false; return; }
    const params = new URLSearchParams(location.search);
    const q = params.get('search');
    if (q !== null) {
      const trimmedQuery = q.trim();
      setFilter('');
      setSearchDraft(trimmedQuery);
      setAppliedSearch(trimmedQuery);
      setOffset(0);
      return;
    }
    if (params.get('browse')) {
      setFilter('');
      setSearchDraft('');
      setAppliedSearch('');
      setOffset(0);
    }
  }, [location]);
  const [loading, setLoading] = useState(true);
  const [visibleLimit, setVisibleLimit] = useState(25);
  // ADMIN-FAHRZEUG-PARTNER-SPERRE-001: EINE Begründungszeile je Fahrzeug für Ablehnen / Sperren / Entfernen.
  const [rejectId, setRejectId] = useState(null);          // Fahrzeug, dessen Begründungszeile offen ist
  const [rejectKind, setRejectKind] = useState('reject');  // reject | lock | delete
  const [rejectReason, setRejectReason] = useState('');
  const openActionRow = (id, kind) => {
    if (rejectId === id && rejectKind === kind) { setRejectId(null); setRejectReason(''); return; }
    setRejectId(id); setRejectKind(kind); setRejectReason('');
  };
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogForm, setCatalogForm] = useState({
    brand: '',
    model: '',
    year_from: '',
    year_to: '',
    vehicle_class: 'comfort',
    seats: 4,
  });

  const loadVehicles = useCallback(() => {
    // Owner-Entscheid 26.08.: Ohne Status/Suche wird der GESAMTE Bestand seitenweise geladen
    // (der frühere Leerlauf „suchgesteuert ohne Liste" ist abgeschafft). Mit Filtern gilt
    // dieselbe echte limit/offset-Paginierung.
    const params = { limit: pageSize, offset };
    if (filter) params.status = filter;
    if (appliedSearch) params.search = appliedSearch;

    setLoading(true);
    api.get('/admin/vehicles', { params })
      .then(response => {
        const list = Array.isArray(response.data.vehicles) ? response.data.vehicles : [];
        const nextTotal = Number(response.data.total);
        const safeTotal = Number.isFinite(nextTotal) && nextTotal >= 0 ? nextTotal : null;
        // Seite hinter dem Bestandsende (z. B. nach Löschung) auf die letzte gültige Seite ziehen.
        if (safeTotal !== null && ((safeTotal === 0 && offset > 0) || (safeTotal > 0 && offset >= safeTotal))) {
          setOffset(safeTotal === 0 ? 0 : Math.floor((safeTotal - 1) / pageSize) * pageSize);
          return;
        }
        setVehicles(list);
        setTotal(safeTotal);
      })
      .catch(() => {
        setVehicles([]);
        toast.error('Fahrzeuge konnten nicht geladen werden');
      })
      .finally(() => setLoading(false));
  }, [filter, appliedSearch, pageSize, offset]);

  const loadCatalog = useCallback(() => {
    setCatalogLoading(true);
    api.get('/admin/vehicle-catalog')
      .then(response => setCatalog(response.data.catalog || []))
      .catch(() => {
        setCatalog([]);
        toast.error('Fahrzeugkatalog konnte nicht geladen werden');
      })
      .finally(() => setCatalogLoading(false));
  }, []);

  useEffect(() => {
    setVisibleLimit(25);
    setRejectId(null);
    loadVehicles();
  }, [loadVehicles]);

  useEffect(() => {
    if (showCatalog) loadCatalog();
  }, [showCatalog, loadCatalog]);

  const applySearch = () => {
    const next = searchDraft.trim();
    if (next === appliedSearch) {
      if (offset !== 0) setOffset(0);
      else loadVehicles();
      return;
    }
    setOffset(0);
    setAppliedSearch(next);
  };

  const clearSearch = () => {
    setSearchDraft('');
    setOffset(0);
    if (appliedSearch) setAppliedSearch('');
    else if (offset === 0) loadVehicles();
  };

  // KENNZEICHEN-EINDEUTIG-001 (Ziel D): Bestand sichtbar machen, nicht stillschweigend entscheiden.
  // Gruppen lebender Fahrzeuge mit demselben normalisierten Kennzeichen — über Partner hinweg und
  // im selben Betrieb. Neue Dubletten entstehen nicht mehr; die alten räumt der Partner über
  // „Fahrzeug entfernen" auf. Ohne Dubletten bleibt der Bereich unsichtbar.
  const [duplicates, setDuplicates] = useState([]);
  const loadDuplicates = useCallback(() => {
    api.get('/admin/vehicles/duplicates')
      .then(response => setDuplicates(Array.isArray(response.data?.groups) ? response.data.groups : []))
      .catch(() => setDuplicates([]));
  }, []);
  useEffect(() => { loadDuplicates(); }, [loadDuplicates]);

  const [reviewDocId, setReviewDocId] = useState(null);   // Prüfbereich an Ort und Stelle
  const refresh = () => {
    loadVehicles();
    loadDuplicates();
    if (showCatalog) loadCatalog();
  };

  // Fahrzeugfreigabe und Ablehnung bleiben ausschließlich auf den dedizierten
  // Lifecycle-Routen mit freigaben.manage und Pflichtdokument-Gate.
  const decide = async (id, action, reason) => {
    try {
      await api.patch(`/admin/vehicles/${id}/${action}`, reason ? { reason } : {});
      toast.success(action === 'approve' ? 'Genehmigt' : 'Abgelehnt');
      setRejectId(null);
      setRejectReason('');
      loadVehicles();
    } catch (error) {
      toast.error(decisionErrorMessage(error.response?.data), {
        duration: 9000,
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  // ADMIN-FAHRZEUG-PARTNER-SPERRE-001: Sperren (Grund Pflicht), Entsperren, Entfernen (weiche Löschung).
  const lockVehicle = async (id, reason) => {
    if (!reason.trim()) { toast.error('Sperrgrund ist Pflicht'); return; }
    try {
      const response = await api.post(`/admin/vehicles/${id}/lock`, { reason: reason.trim() });
      toast.success(response.data?.message || 'Fahrzeug gesperrt');
      setRejectId(null); setRejectReason('');
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Sperren fehlgeschlagen');
    }
  };
  const unlockVehicle = async (id) => {
    try {
      const response = await api.delete(`/admin/vehicles/${id}/lock`, { data: {} });
      toast.success(response.data?.message || 'Fahrzeug entsperrt');
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Entsperren fehlgeschlagen');
    }
  };
  const removeVehicle = async (id, reason) => {
    try {
      const response = await api.delete(`/admin/vehicles/${id}`, { data: reason.trim() ? { reason: reason.trim() } : {} });
      toast.success(response.data?.message || 'Fahrzeug entfernt');
      setRejectId(null); setRejectReason('');
      loadVehicles();
      loadDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Entfernen fehlgeschlagen');
    }
  };

  const setClass = async (id, vehicleClass) => {
    try {
      await api.patch(`/admin/vehicles/${id}`, {
        action: 'set_class',
        vehicle_class: vehicleClass,
      });
      toast.success(`Klasse: ${CLASS_LABELS[vehicleClass]}`);
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Klasse konnte nicht geändert werden');
    }
  };

  const addCatalog = async () => {
    if (!catalogForm.brand || !catalogForm.model) {
      toast.error('Marke und Modell erforderlich');
      return;
    }
    try {
      await api.post('/admin/vehicle-catalog', catalogForm);
      toast.success('Katalog-Eintrag hinzugefügt');
      setCatalogForm({
        brand: '',
        model: '',
        year_from: '',
        year_to: '',
        vehicle_class: 'comfort',
        seats: 4,
      });
      loadCatalog();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const deleteCatalog = async id => {
    try {
      await api.delete(`/admin/vehicle-catalog/${id}`);
      loadCatalog();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Katalog-Eintrag konnte nicht gelöscht werden');
    }
  };

  // serverPaged: neues Backend meldet total → echte Seiten; sonst (altes Backend) bleibt
  // das bisherige client-seitige „Weitere 25 anzeigen"-Verhalten unverändert erhalten.
  const serverPaged = total !== null;
  const visibleVehicles = serverPaged ? vehicles : vehicles.slice(0, visibleLimit);
  const fallbackCount = vehicles.filter(vehicle => vehicle.class_source === 'fallback').length;
  const currentFilter = FILTERS.find(item => item.key === filter) || FILTERS[0];
  const pageStart = serverPaged && total > 0 ? offset + 1 : 0;
  const pageEnd = serverPaged ? Math.min(offset + vehicles.length, total) : 0;

  return (
    <div className="vehicles-page">
      {reviewDocId && <ReviewDrawerById documentId={reviewDocId} onClose={() => setReviewDocId(null)} onDecided={refresh} />}
      <PageHeader
        title="Fahrzeuge"
        description="Fahrzeuge systemweit prüfen, klassifizieren und sicher für den Betrieb freigeben."
        actions={
        <div className="vehicles-heading-actions">
          <span className="vehicles-loaded-badge"><Car size={12} /> {vehicles.length} geladen</span>
          {fallbackCount > 0 && (
            <span className="vehicles-fallback-badge"><Wrench size={12} /> {fallbackCount} Klassenprüfung</span>
          )}
          <span><Gauge size={12} /> max. {serverPaged ? pageSize : 25} pro Seite</span>
          <button onClick={refresh}><RefreshCw size={13} /> Aktualisieren</button>
        </div>
      }
      />

      <section className="vehicles-workspaces" aria-label="Fahrzeug-Arbeitsbereiche">
        {FILTERS.map(item => (
          <button
            key={item.key}
            className={filter === item.key ? 'is-active' : ''}
            onClick={() => { setOffset(0); setFilter(item.key); }}
          >
            {item.key === 'pending' && <ShieldCheck size={16} />}
            {item.key === 'approved' && <Check size={16} />}
            {item.key === 'rejected' && <X size={16} />}
            {item.key === 'retired' && <Trash2 size={16} />}
            {item.key === '' && <Search size={16} />}
            <span><strong>{item.label}</strong><small>{item.hint}</small></span>
          </button>
        ))}
      </section>

      <section className="vehicles-control-panel">
        <div className="vehicles-control-copy">
          <span>{filter === 'pending' ? <FileCheck2 size={15} /> : <Search size={15} />}</span>
          <div>
            <small>{filter ? 'Statusqueue' : 'Gesamtbestand'}</small>
            <strong>{currentFilter.label}</strong>
          </div>
        </div>

        <label className="vehicles-search-field">
          <span>Kennzeichen, Marke oder Modell</span>
          <div>
            <Search size={14} />
            <input
              value={searchDraft}
              onChange={event => setSearchDraft(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && applySearch()}
              placeholder="Kennzeichen, Marke oder Modell …"
            />
          </div>
        </label>

        <div className="vehicles-search-actions">
          <button className="is-search" onClick={applySearch}><Search size={13} /> Suchen</button>
          <button className="is-clear" onClick={clearSearch}>Zurücksetzen</button>
        </div>

        <div className="vehicles-result-stat">
          <small>Treffer gesamt</small>
          <strong>{serverPaged ? total.toLocaleString('de-DE') : vehicles.length}</strong>
          <span>{serverPaged ? 'seitenweise vom Server' : 'serverseitig begrenzt'}</span>
        </div>
        <div className="vehicles-result-stat">
          <small>Aktuell sichtbar</small>
          <strong>{visibleVehicles.length}</strong>
          <span>{serverPaged ? `Seite mit max. ${pageSize}` : 'zunächst max. 25'}</span>
        </div>
        <div className="vehicles-result-stat">
          <small>Klassen-Fallback</small>
          <strong>{fallbackCount}</strong>
          <span>manuell prüfen</span>
        </div>
      </section>

      {duplicates.length > 0 && (
        <section className="vehicles-duplicates" data-testid="vehicle-duplicates" aria-label="Kennzeichen-Dubletten"
          style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            <div>
              <span className="vehicles-overline">Bestand · Restposten</span>
              <h2 style={{ margin: '2px 0 0', fontSize: 16 }}>Kennzeichen-Dubletten ({duplicates.length})</h2>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#7c2d12', maxWidth: 560 }}>
              Dasselbe Kennzeichen ist mehrfach als Fahrzeug angelegt (Altbestand vor der Eindeutigkeitsregel). Neue Dubletten entstehen nicht mehr. Der jeweilige Partner entfernt das falsche Fahrzeug im Partner-Portal unter „Fahrzeuge"; solange die Dublette lebt, ist keine Freigabe möglich.
            </p>
          </header>
          {duplicates.map(group => (
            <div key={group.plate_key} data-testid={`vehicle-duplicate-${group.plate_key}`}
              style={{ borderTop: '1px solid #fed7aa', paddingTop: 8, marginTop: 8 }}>
              <strong style={{ fontSize: 13 }}>Kennzeichen {group.plate_key}</strong>
              <table style={{ width: '100%', marginTop: 6, fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#4b5563' }}>
                    <th style={{ padding: '4px 6px' }}>Kennzeichen</th><th style={{ padding: '4px 6px' }}>Partner</th><th style={{ padding: '4px 6px' }}>Status</th><th style={{ padding: '4px 6px' }}>Fahrer</th><th style={{ padding: '4px 6px' }}>Angelegt am</th>
                  </tr>
                </thead>
                <tbody>
                  {group.vehicles.map(vehicle => (
                    <tr key={vehicle.id}>
                      <td style={{ padding: '4px 6px', fontWeight: 700 }}>{vehicle.license_plate}</td>
                      <td style={{ padding: '4px 6px' }}>{vehicle.company_name || 'Kein Partner'}</td>
                      <td style={{ padding: '4px 6px' }}>{(STATUS[vehicle.approval_status] || STATUS.pending).label}</td>
                      <td style={{ padding: '4px 6px' }}>{vehicle.driver_name || 'Nicht zugewiesen'}</td>
                      <td style={{ padding: '4px 6px' }}>{nurDatum(vehicle.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      )}

      <section className="vehicles-catalog-zone">
        <button className="vehicles-catalog-toggle" onClick={() => setShowCatalog(value => !value)}>
          {showCatalog ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          Fahrzeug-Katalog & Klassenregeln
        </button>
        <span>Marke, Modell und Baujahr steuern die automatische VOOGO-Klasse.</span>
      </section>

      {showCatalog && (
        <section className="vehicles-catalog-panel">
          <header>
            <div>
              <span className="vehicles-overline">Automatische Klassifizierung</span>
              <h2>Fahrzeug-Katalog</h2>
            </div>
            <p>Kein Treffer bedeutet VOOGO Basis mit sichtbarer Admin-Prüfung.</p>
          </header>

          <div className="vehicles-catalog-form">
            <CatalogInput
              label="Marke"
              value={catalogForm.brand}
              onChange={value => setCatalogForm({ ...catalogForm, brand: value })}
              placeholder="Volkswagen"
            />
            <CatalogInput
              label="Modell"
              value={catalogForm.model}
              onChange={value => setCatalogForm({ ...catalogForm, model: value })}
              placeholder="Passat"
            />
            <CatalogInput
              label="Baujahr von"
              value={catalogForm.year_from}
              onChange={value => setCatalogForm({ ...catalogForm, year_from: value })}
              placeholder="2017"
            />
            <CatalogInput
              label="Baujahr bis"
              value={catalogForm.year_to}
              onChange={value => setCatalogForm({ ...catalogForm, year_to: value })}
              placeholder="9999"
            />
            <label className="vehicles-catalog-field">
              <span>Klasse</span>
              <select
                aria-label="Katalogklasse"
                value={catalogForm.vehicle_class}
                onChange={event => setCatalogForm({
                  ...catalogForm,
                  vehicle_class: event.target.value,
                })}
              >
                {CLASS_OPTS.map(vehicleClass => (
                  <option key={vehicleClass} value={vehicleClass}>{CLASS_LABELS[vehicleClass]}</option>
                ))}
              </select>
            </label>
            <CatalogInput
              label="Sitze"
              value={catalogForm.seats}
              onChange={value => setCatalogForm({ ...catalogForm, seats: value })}
            />
            <button className="vehicles-catalog-add" onClick={addCatalog}><Plus size={13} /> Regel hinzufügen</button>
          </div>

          <div className="vehicles-catalog-list">
            {catalogLoading ? (
              <div className="vehicles-catalog-loading">Katalog wird geladen …</div>
            ) : catalog.length === 0 ? (
              <Empty icon={LibraryBig} title="Katalog leer" hint="Eine Klassenregel hinzufügen." />
            ) : (
              <table>
                <thead><tr><th>Marke & Modell</th><th>Baujahr</th><th>VOOGO-Klasse</th><th>Sitze</th><th>Aktion</th></tr></thead>
                <tbody>
                  {catalog.map(entry => (
                    <tr key={entry.id}>
                      <td><strong>{entry.brand} {entry.model}</strong></td>
                      <td>ab {entry.year_from}{entry.year_to < 9999 ? `–${entry.year_to}` : ''}</td>
                      <td><span>{CLASS_LABELS[entry.vehicle_class]}</span></td>
                      <td>{entry.seats} Sitze</td>
                      <td>
                        <button
                          aria-label={`${entry.brand} ${entry.model} aus Katalog löschen`}
                          onClick={() => deleteCatalog(entry.id)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      <section className="vehicles-directory-card">
        <header>
          <div>
            <span className="vehicles-overline">Fahrzeugbestand · {currentFilter.label}</span>
            <h2>{filter === 'pending' ? 'Offene Fahrzeugprüfungen' : currentFilter.label}</h2>
          </div>
          <span><ShieldCheck size={12} /> Dokumentengebundene Freigabe</span>
        </header>

        <div className="vehicles-table-scroll">
          <table className="vehicles-directory-table">
            <thead>
              <tr>
                <th>Fahrzeug</th>
                <th>Partner</th>
                <th>Fahrer</th>
                <th>Klasse / Sitze</th>
                <th>Pflichtdokumente</th>
                <th>Status</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonTableRows rows={4} cols={7} />
              ) : visibleVehicles.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    {/* Der Such-Pflicht-Hinweis bleibt NUR für das alte Backend ohne
                        Paginierungsfelder (dort liefert die Browse-Anfrage weiterhin []). */}
                    <Empty
                      icon={!filter && !appliedSearch && !serverPaged ? Search : Car}
                      title={!filter && !appliedSearch && !serverPaged ? 'Fahrzeug gezielt suchen' : 'Keine Treffer'}
                      hint={!filter && !appliedSearch && !serverPaged
                        ? 'Kennzeichen, Marke oder Modell eingeben.'
                        : 'Suchbegriff oder Arbeitsbereich anpassen.'}
                    />
                  </td>
                </tr>
              ) : visibleVehicles.map(vehicle => {
                const status = STATUS[vehicle.approval_status] || STATUS.pending;
                return (
                  <React.Fragment key={vehicle.id}>
                    <tr>
                      <td>
                        <div className="vehicles-identity-cell">
                          <span>{initials(vehicle.brand)}</span>
                          <div>
                            <strong>{vehicle.brand} {vehicle.model}</strong>
                            <small>{vehicle.license_plate || 'Kein Kennzeichen'} · {vehicle.color || 'Keine Farbe'} · {vehicle.year || '—'}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="vehicles-partner-cell">
                          <strong>{vehicle.company_name || 'Kein Partner'}</strong>
                          <span>{vehicle.owner_name || 'Kein Ansprechpartner'}</span>
                        </div>
                      </td>
                      <td>
                        <div className={`vehicles-driver-cell ${vehicle.driver_name ? 'is-assigned' : ''}`}>
                          <UserRound size={12} />
                          <span>{vehicle.driver_name || 'Nicht zugewiesen'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="vehicles-class-cell">
                          <div>
                            <span>{CLASS_LABELS[vehicle.vehicle_class] || 'VOOGO Basis'}</span>
                            <small>{vehicle.seats || 4} Sitze</small>
                            {vehicle.class_source === 'fallback' && <i>Admin prüfen</i>}
                          </div>
                          <select
                            aria-label={`${vehicle.license_plate} Fahrzeugklasse`}
                            value={vehicle.vehicle_class || 'standard'}
                            onChange={event => setClass(vehicle.id, event.target.value)}
                          >
                            {CLASS_OPTS.map(vehicleClass => (
                              <option key={vehicleClass} value={vehicleClass}>{CLASS_LABELS[vehicleClass]}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td>
                        <div className="vehicles-document-stack">
                          {VEHICLE_DOCUMENTS.map(({ type, label }) => {
                            const document = latestVehicleDocument(vehicle.documents, type);
                            return (
                              <VehicleDocumentEntry
                                key={type}
                                document={document}
                                label={label}
                                onOpen={() => setReviewDocId(document.id)}
                              />
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <span className="vehicles-status" style={{ '--vehicle-status': status.color }}>
                          <i /> {status.label}
                        </span>
                        {vehicle.approval_status === 'suspended' && vehicle.suspended_reason && (
                          <small className="vehicles-status-reason">Grund: {vehicle.suspended_reason}</small>
                        )}
                        {vehicle.approval_status === 'rejected' && vehicle.rejection_reason && (
                          <small className="vehicles-status-reason">Grund: {vehicle.rejection_reason}</small>
                        )}
                      </td>
                      <td>
                        {/* ADMIN-FAHRZEUG-PARTNER-SPERRE-001 (Owner 06.10.2026): Aktionen für JEDEN Status —
                            nicht mehr nur „in Prüfung". Entfernt = Beleg, dort nichts mehr. */}
                        {!['retired', 'draft'].includes(vehicle.approval_status) && (
                          <div className="vehicles-row-actions">
                            {['pending', 'rejected'].includes(vehicle.approval_status) && (
                              <button className="is-approve" onClick={() => decide(vehicle.id, 'approve')}>Genehmigen</button>
                            )}
                            {vehicle.approval_status === 'approved' && (
                              <button className="is-lock" onClick={() => openActionRow(vehicle.id, 'lock')}>Sperren</button>
                            )}
                            {vehicle.approval_status === 'suspended' && (
                              <button className="is-unlock" onClick={() => unlockVehicle(vehicle.id)}>Entsperren</button>
                            )}
                            {['pending', 'approved', 'suspended'].includes(vehicle.approval_status) && (
                              <button className="is-reject" onClick={() => openActionRow(vehicle.id, 'reject')}>Ablehnen</button>
                            )}
                            <button
                              className="is-delete"
                              aria-label={`${vehicle.brand} ${vehicle.model} ${vehicle.license_plate || ''} entfernen`}
                              title="Fahrzeug entfernen (weiche Löschung, Kennzeichen wird frei)"
                              onClick={() => openActionRow(vehicle.id, 'delete')}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {rejectId === vehicle.id && (
                      <tr className={`vehicles-reject-row is-${rejectKind}`}>
                        <td colSpan={7}>
                          <div>
                            {rejectKind === 'delete' && (
                              <span className="vehicles-action-hint">
                                Fahrzeug aus der Flotte entfernen: Kennzeichen wird frei, Fahrer wird gelöst, Unterlagen bleiben als Beleg.
                              </span>
                            )}
                            <input
                              value={rejectReason}
                              onChange={event => setRejectReason(event.target.value)}
                              placeholder={rejectKind === 'lock'
                                ? 'Sperrgrund eingeben (Pflicht, der Partner sieht ihn) …'
                                : rejectKind === 'delete'
                                  ? 'Grund für das Entfernen (optional, der Partner sieht ihn) …'
                                  : 'Ablehnungsgrund eingeben …'}
                            />
                            <button
                              className="vehicles-reject-submit"
                              onClick={() => (rejectKind === 'lock'
                                ? lockVehicle(vehicle.id, rejectReason)
                                : rejectKind === 'delete'
                                  ? removeVehicle(vehicle.id, rejectReason)
                                  : decide(vehicle.id, 'reject', rejectReason))}
                            >
                              {rejectKind === 'lock' ? 'Sperren' : rejectKind === 'delete' ? 'Entfernen' : 'Ablehnen'}
                            </button>
                            <button onClick={() => {
                              setRejectId(null);
                              setRejectReason('');
                            }}
                            >
                              Abbrechen
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Neues Backend (total vorhanden): echte Seitennavigation + Seitengröße 25/50/100. */}
        {!loading && serverPaged && total > 0 && (
          <footer className="vehicles-load-more" aria-label="Fahrzeugseiten">
            <span>{pageStart}–{pageEnd} von {total.toLocaleString('de-DE')} Fahrzeugen</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4b5563' }}>
                <select
                  aria-label="Einträge pro Seite"
                  value={pageSize}
                  onChange={(event) => { setPageSize(Number(event.target.value)); setOffset(0); }}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151' }}
                >
                  {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
                pro Seite
              </label>
              <button disabled={offset === 0} onClick={() => setOffset(value => Math.max(0, value - pageSize))}>
                Zurück
              </button>
              <button disabled={offset + pageSize >= total} onClick={() => setOffset(value => value + pageSize)}>
                Weiter
              </button>
            </div>
          </footer>
        )}
        {/* Altes Backend ohne total: unverändertes client-seitiges Nachladen. */}
        {!loading && !serverPaged && vehicles.length > visibleVehicles.length && (
          <footer className="vehicles-load-more">
            <span>{visibleVehicles.length} von {vehicles.length} sichtbar</span>
            <button onClick={() => setVisibleLimit(limit => Math.min(limit + 25, vehicles.length))}>
              Weitere {Math.min(25, vehicles.length - visibleVehicles.length)} anzeigen
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}
